import React, { useState, useEffect } from "react";
import { ArrowRight, Sparkles, SparklesIcon } from "lucide-react";
import { useSetAtom } from "jotai";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { proDialog } from "@/state/dialogs";
import { useRouter } from "@tanstack/react-router";
import { PATHS } from "@/config/paths";
import { getTranslatedPricing, IOS_PRICING } from "@/config/pricing";
import { Switch } from "@/components/ui/switch";
import { getPriceDisplayIOS, getPriceDisplayWeb } from "@/components/pro/Pro.helpers";
import { useIsIOS } from "@/hooks/useIsIOS";
import { useTranslation } from "@/hooks/useTranslation";
import { useMobile } from "@/providers/mobileProvider";
import { Heading } from "@/components/ui/heading";
import { useIAP } from "@/providers/IAPProvider";
import type { IOSProduct, Product } from "@/lib/types";

interface ProgressPricingProps {
  onHome?: boolean;
  className?: string;
}

export function ProgressPricing({ onHome = false, className = "" }: ProgressPricingProps) {
  const router = useRouter();
  const isIOS = useIsIOS();
  const { t, tNode } = useTranslation();
  const setProDialogOpen = useSetAtom(proDialog);
  const [isYearly, setIsYearly] = useState(true);
  const translatedPricing = getTranslatedPricing(t);
  const { IOSproducts } = useIAP();
  const [monthlyProduct, setMonthlyProduct] = useState<IOSProduct | null>(null);
  const [yearlyProduct, setYearlyProduct] = useState<IOSProduct | null>(null);

  useEffect(() => {
    if (IOSproducts.length > 0) {
      const monthly = IOSproducts.find(
        (p) => p.attributes.offerName === IOS_PRICING.monthly_subscription,
      );
      const yearly = IOSproducts.find(
        (p) => p.attributes.offerName === IOS_PRICING.yearly_subscription,
      );
      setMonthlyProduct(monthly || null);
      setYearlyProduct(yearly || null);
    }
  }, [IOSproducts]);

  const priceDisplay = isIOS
    ? getPriceDisplayIOS(isYearly, yearlyProduct, monthlyProduct)
    : getPriceDisplayWeb(isYearly);

  const headingElement = onHome ? "h2" : "h1";

  return (
    <section id="pricing" className={`col-span-12 space-y-8 grid grid-cols-12 ${className}`}>
      <div className="col-span-12 flex flex-col items-center gap-4 mb-6 md:mb-12 text-center">
        <SparklesIcon className="h-8 w-8 text-amber-500" />

        <Heading level={headingElement as "h1" | "h2"} size="h2">
          {t("pricing.page.title")}
        </Heading>

        <p className="max-w-xl text-lg text-pretty leading-8 text-foreground">
          {tNode("pricing.page.description", {
            healthTransformation: (
              <span className="text-primary">{t("pricing.page.health_transformation")}</span>
            ),
          })}
        </p>
      </div>

      <div className="col-span-12 flex items-center justify-center gap-4 md:mb-4">
        <div className="flex items-center gap-2 bg-muted/50 rounded-full p-1 pr-3">
          <button
            data-testid="monthly-button"
            type="button"
            onClick={() => setIsYearly(false)}
            className={`text-sm px-3 py-1 rounded-full transition-colors ${!isYearly ? "bg-white dark:bg-black text-primary shadow-xs" : "text-muted-foreground hover:text-muted-foreground/80"}`}
          >
            {t("pricing.page.monthly")}
          </button>
          <Switch
            checked={isYearly}
            onCheckedChange={setIsYearly}
            aria-label="Toggle billing period"
          />
          <button
            data-testid="yearly-button"
            type="button"
            onClick={() => setIsYearly(true)}
            className={`text-sm px-3 py-1 rounded-full transition-colors ${isYearly ? "bg-white dark:bg-black text-primary shadow-xs" : "text-muted-foreground hover:text-muted-foreground/80"}`}
          >
            {t("pricing.page.yearly")}
          </button>
        </div>
        <span className="text-xs text-primary bg-primary/10 px-3 py-1.5 rounded-full font-medium">
          {t("pricing.page.save_percentage")}
        </span>
      </div>

      <div className="col-span-12 grid grid-cols-12 gap-8 md:gap-6 mt-4">
        {/* Free Tier */}
        <div className="col-span-12 sm:col-span-6 lg:col-start-3 lg:col-span-4">
          <Card className="relative p-6 flex flex-col group overflow-hidden bg-gradient-to-br from-background via-background to-muted/10 border border-border/40 shadow-sm hover:shadow-xl transition-all duration-500 hover:border-primary/30 dark:hover:border-primary/30 hover:-translate-y-1">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/5 to-transparent rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

            <div className="space-y-6">
              <div>
                <Heading level="h3" size="h5" className="font-medium">
                  {translatedPricing.free.name}
                </Heading>
                <p className="text-muted-foreground mt-2">{translatedPricing.free.description}</p>
              </div>

              <div className="flex items-baseline">
                <span className="text-4xl font-bold">€{translatedPricing.free.price}</span>
                <span className="text-muted-foreground ml-1">/{translatedPricing.free.period}</span>
              </div>

              <div>
                <Button
                  data-testid="get-started-button"
                  variant="outline"
                  className="w-full"
                  onClick={() => router.navigate({ to: PATHS.DASHBOARD })}
                >
                  {t("pricing.page.get_started")}
                  <ArrowRight />
                </Button>
              </div>
            </div>

            <div className="mt-6 space-y-3 flex-1">
              {translatedPricing.free.features.map((feature) => (
                <div key={feature.text} className="flex items-center gap-3">
                  <div className="rounded-full bg-primary/10 p-1">
                    {React.createElement(feature.icon, {
                      className:
                        "size-4 text-primary shrink-0 group-hover:text-primary/80 transition-colors duration-300",
                    })}
                  </div>
                  <span className="text-sm">{feature.text}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Pro Tier */}
        <div className="col-span-12 sm:col-span-6 lg:col-span-4">
          <Card className="relative p-6 flex flex-col group bg-gradient-to-br from-background via-background to-muted/10 border border-border/40 shadow-sm hover:shadow-xl transition-all duration-500 hover:border-primary/30 hover:-translate-y-1 dark:hover:border-primary/30">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/5 to-transparent rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

            <div className="absolute -top-3 left-6 flex gap-2">
              <span className="inline-block rounded-full bg-primary px-3 py-0.5 text-xs font-bold text-primary-foreground shadow-xs">
                {t("pricing.page.summer_deal")}
              </span>
            </div>

            <div className="space-y-6">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Heading level="h3" size="h5" className="text-primary font-bold">
                    {translatedPricing.pro.name}
                  </Heading>
                  <Sparkles className="size-4 text-amber-500 group-hover:text-amber-400 transition-colors duration-300" />
                </div>
                <p className="text-muted-foreground">{translatedPricing.pro.description}</p>
              </div>

              <div className="space-y-2">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold" data-testid="yearly-price">
                    {priceDisplay.price}
                  </span>
                  <span className="text-muted-foreground">/{priceDisplay.period}</span>
                  {isYearly && (
                    <span className="text-xs text-muted-foreground">
                      {t("pricing.page.billed_yearly")}
                    </span>
                  )}
                </div>
              </div>

              <div>
                <Button
                  data-testid="upgrade-to-pro-button"
                  className="w-full bg-linear-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
                  onClick={() => setProDialogOpen(true)}
                >
                  {t("pricing.page.upgrade_to_pro")}
                  <ArrowRight />
                </Button>
              </div>
            </div>

            <div className="mt-6 space-y-3 flex-1">
              {translatedPricing.pro.features.map((feature) => (
                <div key={feature.text} className="flex items-center gap-3">
                  <div className="rounded-full bg-primary/10 p-1">
                    {React.createElement(feature.icon, {
                      className:
                        "size-3 text-primary group-hover:text-primary/80 transition-colors duration-300",
                    })}
                  </div>
                  <span className="text-sm">{feature.text}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}
